import React, { useContext, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import { useHistory, useParams } from "react-router-dom";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { ProductContext } from "../../../contexts/ProductContext";
import { StyledMenu, StyledMenuItem } from "../../../components/Menu/StyledMenu";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomButton from "../../../components/Button/CustomButton";
import { CustomTableRowData } from "../../../components/CustomTable";
import CustomProgress from "../../../components/Alert/CustomProgress";
import CustomFooterGraphic from "../CustomFooterGraphic";
import ProductSampleAddDialog from "./ProductSampleAddDialog";
import ProductSampleDetailsDialog from "./ProductSampleDetailsDialog";
import ProductSampleAddCompetitorDialog from "./ProductSampleAddCompetitorDialog";
import ProductSampleDetailsCompetitorDialog from "./ProductSampleDetailsCompetitorDialog";
import ProductSampleAddPrototypeDialog from "./ProductSampleAddPrototypeDialog";
import ProductSampleDetailsPrototypeDialog from "./ProductSampleDetailsPrototypeDialog";
import ProductSampleAddUnknownProductDialog from "./ProductSampleAddUnknownProductDialog";
import ProductSampleDetailsUnknownProductDialog from "./ProductSampleDetailsUnknownProductDialog";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import UnknownProductSampleOverviewTable from "./UnknownProductSampleOverviewTable";
import { tesaDarkRedError } from "../../../styles/colors";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import GenericPermissionContext from "../../../contexts/GenericPermissionContext";
import { fadeIn } from "../../../styles/springs";
import ProductSampleAddExistingProductDialog from "./ProductSampleAddExistingProductDialog";
import TesaProductSampleOverviewTable from "./TesaProductSampleOverviewTable";
import CompetitorProductSampleOverviewTable from "./CompetitorProductSampleOverviewTable";
import TesaPrototypeSampleOverviewTable from "./TesaPrototypeSampleOverviewTable";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import InfoBox from "../../../components/InfoBox";
import { GalleryContextProvider } from "../../../contexts/GalleryContext";
import ProductSuggestions from "./ProductSuggestions";
import ProductReferenceResponse from "../../../api/models/ProductReferenceResponse";
import ProductEditData from "../DescriptionTab/ProductEditData.model";
import ProductSuggestionsDialog from "../DescriptionTab/dialogs/ProductSuggestionDialog";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";
import { ProductSampleReference } from "../../../api/models/product/ProductSampleReference";
import ProductSampleDTO from "../../../api/models/product/ProductSampleDTO";
import ProductType from "../../../api/models/product/ProductType";
import { HttpErrorType } from "../../../models/HttpErrorType";
import { CompetitorProductReferenceResponse } from "../../../api/models/CompetitorProductReference";
import { PrototypeProductReferenceResponse } from "../../../api/models/PrototypeProductReference";
import PrototypeSuggestionDialog from "../DescriptionTab/dialogs/PrototypeSuggestionDialog";
import CompetitorSuggestionDialog from "../DescriptionTab/dialogs/CompetitorSuggestionDialog";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";
import { TesaPrototype } from "../../../api/models/product/TesaPrototype";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginBottom: {
      marginBottom: theme.spacing(3),
    },
    loading: {
      "& .MuiCircularProgress-root": {
        position: "fixed",
        margin: "auto",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      },
      "& .mt-0": {
        marginTop: 0,
      },
    },
  }),
);

const INFO_NEW_PRODUCT_SAMPLE =
  "The Product Sample is the actual sample (roll) that you have in front of you and that you will use for the Test " +
  "Request. " +
  "*Choose 'tesa Product' to create a product sample based on an existing tesa product. " +
  "*Choose 'tesa Prototype' to create a product sample based on a prototype from a tesa development laboratory. " +
  "*Choose 'Competitor Product' to create a product sample based on a product from any competitor company. " +
  "*Choose 'Unknown Product' to create a product sample based on an unknown product (e.g. if you received a " +
  "competitor roll from a customer without having a name or if you need to test a not listed tesa product).";

const ProductTab: React.FunctionComponent = () => {
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductSamplesApi(api);
  const history = useHistory();
  const { testRequestId, dialog } = useParams<RequestsPagePathParams>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(dialog === "addProduct" || false);
  const [isAddPrototypeDialogOpen, setIsAddPrototypeDialogOpen] = useState(dialog === "addProductPrototype" || false);
  const [isAddCompetitorDialogOpen, setIsAddCompetitorDialogOpen] = useState(
    dialog === "addProductCompetitor" || false,
  );
  const [isAddUnknownProductDialogOpen, setIsAddUnknownProductDialogOpen] = useState(
    dialog === "addUnknownProduct" || false,
  );
  const [isAddExistingProductDialogOpen, setIsAddExistingProductDialogOpen] = useState(false);
  const [isCloneExistingProductDialogOpen, setIsCloneExistingProductDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(dialog === "detailProduct" || false);
  const [isDetailPrototypeDialogOpen, setIsDetailPrototypeDialogOpen] = useState(
    dialog === "detailProductPrototype" || false,
  );
  const [isDetailCompetitorDialogOpen, setIsDetailCompetitorDialogOpen] = useState(
    dialog === "detailProductCompetitor" || false,
  );
  const [isUnknownProductDetailDialogOpen, setIsUnknownProductDetailDialogOpen] = useState(
    dialog === "detailUnknownProduct" || false,
  );
  const [isSuggestionDialogOpen, setIsSuggestionDialogOpen] = useState(false);
  const [isPrototypeSuggestionDialogOpen, setIsPrototypeSuggestionDialogOpen] = useState(false);
  const [isCompetitorSuggestionDialogOpen, setIsCompetitorSuggestionDialogOpen] = useState(false);
  const [sampleDetailsLoading, setSampleDetailsLoading] = useState(false);
  const [suggestionInitialData, setSuggestionInitialData] = useState<
    (ProductReferenceResponse & CompetitorProductReferenceResponse & PrototypeProductReferenceResponse) | null
  >(null);
  const [apiReqType, setApiReqType] = useState("POST");
  const { testRequest, updateTestRequest, updateProductObject } = useContext(ProductContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const classes = useStyles();
  const springProps = useSpring(fadeIn);
  const { handleHttpError, handleDialogNegativeCallback } = useContext(ErrorSnackbarContext);
  const { accessProductSamples, newProductSample, productSamples, updatePermissionState } =
    useContext(TestRequestPermissionContext);
  const { accessProducts } = useContext(GenericPermissionContext);

  const [productProperties, setProductProperties] = useState({
    nart: "",
    productName: "",
    baseProduct: "",
  });

  const [competitorProperties, setCompetitorProperties] = useState<CompetitorProduct | null>(null);
  const [prototypeProperties, setPrototypeProperties] = useState<TesaPrototype | null>(null);

  useEffect(() => {
    // remove postfixes from current path
    history.push(`/requests/${testRequestId}/2`);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openDetailsDialog = (productType: ProductType) => {
    switch (productType) {
      case "COMPETITOR_PRODUCT":
        setIsDetailCompetitorDialogOpen(true);
        break;
      case "TESA_PRODUCT":
        setIsDetailDialogOpen(true);
        break;
      case "TESA_PROTOTYPE":
        setIsDetailPrototypeDialogOpen(true);
        break;
      case "UNKNOWN_PRODUCT":
        setIsUnknownProductDetailDialogOpen(true);
        break;
      default:
      // do nothing
    }
  };

  const handleEditById = async (
    productSampleId: number,
    additionalActions?: (sampleData: ProductSampleDTO, usedIn: boolean) => void,
  ) => {
    setSampleDetailsLoading(true);

    try {
      const { data: productSampleData } = await productSamplesApi.fetchProductSampleById(
        testRequestId,
        productSampleId,
      );

      const { data: testRequestData } = await testRequestApi.getTestRequest(testRequestId);

      const { productName, baseProduct } =
        testRequestData.description.productReferences?.find(
          (productReference) => productReference.nart === (productSampleData as any).nart,
        ) || {};

      const sampleRef = testRequest.productSampleReferences.find(
        (psr: ProductSampleReference) => psr.productSample.id === productSampleId,
      );

      if (sampleRef) {
        updateProductObject({
          ...productSampleData,
          productName,
          baseProduct,
          usedIn: sampleRef.usedIn,
        });
        additionalActions?.(productSampleData, sampleRef.usedIn);
        openDetailsDialog(productSampleData.type);
      }
    } catch (error) {
      handleHttpError(error as HttpErrorType);
    } finally {
      setSampleDetailsLoading(false);
    }
  };

  const refresh = () => {
    updatePermissionState()
      .then(() =>
        testRequestApi.getTestRequest(testRequestId).then((res) => {
          updateTestRequest(res.data);
          setIsLoading(false);
        }),
      )
      .catch(handleHttpError);
  };

  useEffect(() => {
    refresh();
  }, []);

  const showProductSampleEditAction = (data: CustomTableRowData) =>
    productSamples?.[data.id]?.editDefinition || productSamples?.[data.id]?.editAttachments || false;

  const showProductSampleViewDetailsAction = (data: CustomTableRowData) => {
    return (!showProductSampleEditAction(data) && productSamples?.[data.id]?.showDetails) || false;
  };

  const showProductSampleDeleteAction = (data: CustomTableRowData) => productSamples?.[data.id]?.delete || false;

  const showSuggestionDetailsDialog = ({
    data,
  }: ProductEditData<
    ProductReferenceResponse & CompetitorProductReferenceResponse & PrototypeProductReferenceResponse
  >) => {
    setSuggestionInitialData(data);
    switch (true) {
      case Boolean(data?.nart || data?.baseProduct || data?.productName):
        setIsSuggestionDialogOpen(true);
        break;
      case Boolean(data?.competitorProductName):
        setIsCompetitorSuggestionDialogOpen(true);
        break;
      case Boolean(data?.prototypeProductName):
        setIsPrototypeSuggestionDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = (id: number) => {
    productSamplesApi
      .deleteTestRequestProducts(testRequest.requestId, [id])
      .then(() => {
        updateProductObject(null);
        refresh();
      })
      .catch((error) => {
        if (error?.response?.data?.errorCode === "PRODUCT_SAMPLE_IN_USE") {
          showConfirmationDialog({
            headline: "Product sample in use",
            confirmText: "Okay",
            text: `This product sample is part of a started test series and can therefore 
                      not be deleted.`,
            hideCancel: true,
          }).catch(handleDialogNegativeCallback);
        } else {
          handleHttpError(error);
        }
      });
  };

  const onDelete = (entry: CustomTableRowData) => {
    showConfirmationDialog({
      text: (
        <>
          You are about to delete <b>{entry.productSampleName}</b>.
        </>
      ),
      confirmText: "Delete",
      confirmColor: tesaDarkRedError,
    })
      .then(() => handleDelete(entry.id))
      .catch(handleDialogNegativeCallback);
  };

  if (!accessProductSamples) {
    return <animated.div style={springProps}>Access denied.</animated.div>;
  }

  return isLoading ? (
    <CustomProgress />
  ) : (
    <animated.div style={springProps}>
      <Box mb={3}>
        <ProductSuggestions
          onTagClick={showSuggestionDetailsDialog}
          values={
            [
              ...testRequest?.description?.productReferences,
              ...testRequest?.description?.prototypeProductReferences,
              ...testRequest?.description?.competitorProductReferences,
            ] || []
          }
        />
      </Box>
      <Box>
        <ProductSampleAddDialog
          onClose={(formComplete) => {
            setIsAddDialogOpen(false);
            if (formComplete) {
              setIsDetailDialogOpen(true);
            }
          }}
          open={isAddDialogOpen}
        />
        <ProductSampleAddCompetitorDialog
          onClose={(formComplete) => {
            setIsAddCompetitorDialogOpen(false);
            if (formComplete) {
              setIsDetailCompetitorDialogOpen(true);
            }
          }}
          open={isAddCompetitorDialogOpen}
        />
        <ProductSampleAddPrototypeDialog
          onClose={(formComplete) => {
            setIsAddPrototypeDialogOpen(false);
            if (formComplete) {
              setIsDetailPrototypeDialogOpen(true);
            }
          }}
          open={isAddPrototypeDialogOpen && accessProducts}
        />
        <ProductSampleAddUnknownProductDialog
          onClose={() => {
            setIsAddUnknownProductDialogOpen(false);
          }}
          onSelected={() => {
            setIsAddUnknownProductDialogOpen(false);
            setIsUnknownProductDetailDialogOpen(true);
          }}
          open={isAddUnknownProductDialogOpen}
        />
        <ProductSampleAddExistingProductDialog
          onClose={() => {
            setIsAddExistingProductDialogOpen(false);
          }}
          onSelected={() => {
            setIsAddExistingProductDialogOpen(false);
            refresh();
          }}
          open={isAddExistingProductDialogOpen}
          action="USE"
        />
        <ProductSampleAddExistingProductDialog
          onClose={() => {
            setIsCloneExistingProductDialogOpen(false);
          }}
          onSelected={() => {
            setIsCloneExistingProductDialogOpen(false);
            refresh();
          }}
          open={isCloneExistingProductDialogOpen}
          action="DUPLICATE"
        />
        <ProductSuggestionsDialog
          readOnly
          open={isSuggestionDialogOpen}
          onClose={() => setIsSuggestionDialogOpen(false)}
          onConfirm={({ nart, productName, baseProduct }) => {
            setProductProperties({ nart, productName, baseProduct });
            setIsSuggestionDialogOpen(false);
            openDetailsDialog("TESA_PRODUCT");
          }}
          preset={suggestionInitialData}
          resetSalesProductInitialData={() => setSuggestionInitialData(null)}
        />
        <PrototypeSuggestionDialog
          preset={suggestionInitialData}
          open={isPrototypeSuggestionDialogOpen}
          onClose={() => {
            setIsPrototypeSuggestionDialogOpen(false);
          }}
          onConfirm={({ prototype }) => {
            setPrototypeProperties(prototype);
            setIsPrototypeSuggestionDialogOpen(false);
            openDetailsDialog("TESA_PROTOTYPE");
          }}
        />
        <CompetitorSuggestionDialog
          preset={suggestionInitialData}
          open={isCompetitorSuggestionDialogOpen}
          onClose={() => {
            setIsCompetitorSuggestionDialogOpen(false);
          }}
          onConfirm={({ product }) => {
            setCompetitorProperties(product);
            setIsCompetitorSuggestionDialogOpen(false);
            openDetailsDialog("COMPETITOR_PRODUCT");
          }}
          resetSalesProductInitialData={() => {}}
        />
        <GalleryContextProvider>
          <ProductSampleDetailsDialog
            testRequestID={testRequest?.requestId || ""}
            productProperties={productProperties}
            onClose={() => {
              updateProductObject(null);
              refresh();
              setIsDetailDialogOpen(false);
            }}
            open={isDetailDialogOpen}
            callback={(close) => setIsDetailDialogOpen(!close)}
          />
        </GalleryContextProvider>
        <GalleryContextProvider>
          <ProductSampleDetailsPrototypeDialog
            apiReqType={apiReqType}
            testRequestID={testRequest?.requestId || ""}
            prototypeProperties={prototypeProperties}
            onClose={() => {
              setApiReqType("POST");
              updateProductObject(null);
              refresh();
              setIsDetailPrototypeDialogOpen(false);
            }}
            open={isDetailPrototypeDialogOpen}
            callback={(close) => setIsDetailPrototypeDialogOpen(!close)}
          />
        </GalleryContextProvider>
        <GalleryContextProvider>
          <ProductSampleDetailsCompetitorDialog
            testRequestID={testRequest?.requestId || ""}
            competitorProperties={competitorProperties}
            onClose={() => {
              updateProductObject(null);
              refresh();
              setIsDetailCompetitorDialogOpen(false);
            }}
            open={isDetailCompetitorDialogOpen}
            callback={(close) => setIsDetailCompetitorDialogOpen(!close)}
          />
        </GalleryContextProvider>
        <GalleryContextProvider>
          <ProductSampleDetailsUnknownProductDialog
            testRequestID={testRequest?.requestId || ""}
            onClose={() => {
              updateProductObject(null);
              refresh();
              setIsUnknownProductDetailDialogOpen(false);
            }}
            open={isUnknownProductDetailDialogOpen}
            callback={(close) => setIsUnknownProductDetailDialogOpen(!close)}
          />
        </GalleryContextProvider>
        {newProductSample && (
          <InfoBox compress text={INFO_NEW_PRODUCT_SAMPLE}>
            <CustomButton
              add
              aria-controls="customized-menu"
              aria-haspopup="true"
              onClick={handleClick}
              text="Add Product Sample"
            />
          </InfoBox>
        )}
        <Box className={classes.marginBottom} />
        {testRequest && (
          <>
            <TesaProductSampleOverviewTable
              onDelete={(tesaProductSample) => onDelete(tesaProductSample as CustomTableRowData)}
              onEdit={handleEditById}
              productReferences={testRequest.description.productReferences}
              productSampleReferences={testRequest.productSampleReferences}
              showProductSampleDeleteAction={showProductSampleDeleteAction}
              showProductSampleEditAction={showProductSampleEditAction}
              showProductSampleViewDetailsAction={showProductSampleViewDetailsAction}
            />
            <TesaPrototypeSampleOverviewTable
              onDelete={(prototypeProductSample) => onDelete(prototypeProductSample as CustomTableRowData)}
              onEdit={handleEditById}
              productSampleReferences={testRequest.productSampleReferences}
              setApiRequestType={setApiReqType}
              showProductSampleDeleteAction={showProductSampleDeleteAction}
              showProductSampleEditAction={showProductSampleEditAction}
              showProductSampleViewDetailsAction={showProductSampleViewDetailsAction}
            />
            <CompetitorProductSampleOverviewTable
              onDelete={(competitorProductSample) => onDelete(competitorProductSample as CustomTableRowData)}
              onEdit={handleEditById}
              productSampleReferences={testRequest.productSampleReferences}
              showProductSampleDeleteAction={showProductSampleDeleteAction}
              showProductSampleEditAction={showProductSampleEditAction}
              showProductSampleViewDetailsAction={showProductSampleViewDetailsAction}
            />
            <UnknownProductSampleOverviewTable
              productSampleReferences={testRequest.productSampleReferences}
              onEdit={handleEditById}
              onDelete={(unknownProductSample) => onDelete(unknownProductSample as CustomTableRowData)}
              showProductSampleDeleteAction={showProductSampleDeleteAction}
              showProductSampleEditAction={showProductSampleEditAction}
              showProductSampleViewDetailsAction={showProductSampleViewDetailsAction}
            />
          </>
        )}
        <>
          <StyledMenu
            id="customized-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsAddDialogOpen(true);
              }}
            >
              <ListItemText primary="tesa Product" />
            </StyledMenuItem>
            {accessProducts && (
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  setIsAddPrototypeDialogOpen(true);
                }}
              >
                <ListItemText primary="tesa Prototype" />
              </StyledMenuItem>
            )}
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsAddCompetitorDialogOpen(true);
              }}
            >
              <ListItemText primary="Competitor Product" />
            </StyledMenuItem>
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsAddUnknownProductDialogOpen(true);
              }}
            >
              <ListItemText primary="Unknown Product" />
            </StyledMenuItem>
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsAddExistingProductDialogOpen(true);
              }}
            >
              <ListItemText primary="Use Existing Product Sample" />
            </StyledMenuItem>
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setIsCloneExistingProductDialogOpen(true);
              }}
            >
              <ListItemText primary="Duplicate Existing Product Sample" />
            </StyledMenuItem>
          </StyledMenu>
        </>
        <CustomFooterGraphic />
      </Box>
      {sampleDetailsLoading && <CustomProgress variant="viewportCentered" />}
    </animated.div>
  );
};

export default ProductTab;
